import * as bootstrap from 'bootstrap';
import { pushDataLayer } from './analytics';
import { initCaptcha } from './captcha';
import { trackTelegramLead } from './telegram-pixel';
import { trackMetaLead } from './meta-pixel';
import { initAcquisitionFields } from './acquisition';

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

// Контекст формы для событий GA4. form_name/form_location ставит обработчик
// клика по CTA-кнопке (см. analytics.js); если форма открыта иначе — берём defaults.
function formMeta(form, defaults) {
  return {
    form_name: form.dataset.gaFormName || defaults.name,
    form_location: form.dataset.gaFormLocation || defaults.location,
    page_path: window.location.pathname,
  };
}

function collectInvalidFields(form) {
  return Array.from(form.querySelectorAll(':invalid'))
    .map((el) => el.name)
    .filter(Boolean)
    .join(',');
}

function trackFormSubmit(form, defaults) {
  pushDataLayer('submit_request', formMeta(form, defaults));
}

function trackFormError(form, defaults, fields) {
  pushDataLayer('form_error', { ...formMeta(form, defaults), error_fields: fields });
}

async function postForm(url, data, token = csrfToken) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-TOKEN': token,
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(data),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw payload;
  }

  return payload;
}

function showFormErrors(form, errors) {
  form.querySelectorAll('.is-invalid').forEach((el) => {
    el.classList.remove('is-invalid');
    delete el.dataset.errorMessage;
  });

  const captchaFeedback = form.querySelector('[data-captcha-error]');
  captchaFeedback?.classList.remove('d-block');

  Object.entries(errors || {}).forEach(([field, messages]) => {
    // У капчи нет своего input — ошибка выводится под виджетом. Бэкенд шлёт
    // i18n-ключ, перевод в локали страницы лежит в data-атрибуте слота.
    if (field === 'captcha_token') {
      if (captchaFeedback && messages?.[0]) {
        captchaFeedback.textContent = captchaFeedback.dataset.captchaErrorMessage || messages[0];
        captchaFeedback.classList.add('d-block');
      }
      return;
    }

    // Поле без собственной рамки (скрытое значение кастомного селекта) показывает ошибку
    // на своём видимом элементе — его называет data-invalid-target.
    const named = form.querySelector(`[name="${field}"]`);
    const input = named?.dataset.invalidTarget ? form.querySelector(named.dataset.invalidTarget) : named;
    if (input) {
      input.classList.add('is-invalid');
      const feedback = input.parentElement?.querySelector('.invalid-feedback');
      if (feedback && messages?.[0]) {
        feedback.textContent = messages[0];
      } else if (messages?.[0]) {
        // У поля нет своего блока сообщения — отдаём текст тултипу напрямую
        // (см. input-tooltip.js, фолбэк на data-error-message).
        input.dataset.errorMessage = messages[0];
      }
    }
  });
}

export function registerForm() {
  const form = document.getElementById('registerForm');
  if (!form) return;

  const captcha = initCaptcha(form, 'register');
  const acquisition = initAcquisitionFields(form);

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    const isAcquisitionValid = acquisition.validate();

    const password = form.querySelector('#registerPassword')?.value;
    const passwordRepeat = form.querySelector('#registerPasswordRepeat')?.value;

    if (password && passwordRepeat && password !== passwordRepeat) {
      form.querySelector('#registerPasswordRepeat')?.classList.add('is-invalid');
      return;
    }

    if (!form.checkValidity() || !isAcquisitionValid) return;

    try {
      const payload = await postForm(form.dataset.action, {
        name: form.querySelector('#registerName')?.value || form.querySelector('#registerEmail')?.value,
        email: form.querySelector('#registerEmail')?.value,
        password,
        password_confirmation: passwordRepeat || password,
        ...acquisition.getValues(),
        captcha_token: await captcha.getToken(),
      });

      if (payload.redirect) {
        window.location.href = payload.redirect;
      }
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
    }
  });
}

// Шаг «откуда узнали» после регистрации через Google (register-source.blade.php).
export function registerSourceForm() {
  const form = document.getElementById('registerSourceForm');
  if (!form) return;

  const acquisition = initAcquisitionFields(form);

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    const isAcquisitionValid = acquisition.validate();
    if (!form.checkValidity() || !isAcquisitionValid) return;

    try {
      const payload = await postForm(form.dataset.action, acquisition.getValues());

      if (payload.redirect) {
        window.location.href = payload.redirect;
      }
    } catch (error) {
      showFormErrors(form, error.errors);
    }
  });
}

export function loginForm() {
  const form = document.getElementById('loginForm');
  if (!form) return;

  const emailInput = form.querySelector('#loginEmail');
  const passwordInput = form.querySelector('#loginPassword');
  const codeInput = form.querySelector('#login2faCode');
  const twoFaBlock = form.querySelector('#login2faBlock');

  // Капча нужна только на шаге с кредами: на шаге 2FA сервер её не требует.
  const captcha = initCaptcha(form, 'login');

  // Токен из meta протухает после первого шага: Auth::attempt регенерирует
  // сессию (и CSRF-токен). Сервер возвращает новый токен в ответе requires_2fa —
  // им и подписываем второй запрос с кодом.
  let token = csrfToken;

  const is2faVisible = () => twoFaBlock && !twoFaBlock.classList.contains('d-none');
  const codeIsComplete = () => /^\d{6}$/.test(codeInput?.value ?? '');

  // Пусто/неполно — серый (нейтральный), ровно 6 цифр — зелёный.
  const syncCodeState = () => {
    if (!codeInput) return;
    codeInput.classList.remove('is-invalid');
    codeInput.classList.toggle('is-valid', codeIsComplete());
  };

  const reveal2fa = () => {
    twoFaBlock?.classList.remove('d-none');
    // На шаге кода валидируем только его: снимаем с формы was-validated, иначе
    // пустое обязательное поле кода сразу подсветится красным.
    form.classList.remove('was-validated');
    emailInput?.removeAttribute('required');
    passwordInput?.removeAttribute('required');
    codeInput?.setAttribute('required', 'required');
    syncCodeState();
    codeInput?.focus();
  };

  codeInput?.addEventListener('input', () => {
    // Только цифры, не более 6.
    codeInput.value = codeInput.value.replace(/\D/g, '').slice(0, 6);
    syncCodeState();
  });

  // Соц-вход: страница отрисована сразу с раскрытым блоком 2FA.
  if (is2faVisible()) reveal2fa();

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    if (is2faVisible()) {
      // Код неполон — молча ждём ввода, поле остаётся серым (без ошибки).
      if (!codeIsComplete()) return;
    } else {
      form.classList.add('was-validated');
      if (!form.checkValidity()) return;
    }

    try {
      const payload = await postForm(form.dataset.action, {
        email: emailInput?.value,
        password: passwordInput?.value,
        code: codeInput?.value,
        captcha_token: is2faVisible() ? null : await captcha.getToken(),
      }, token);

      if (payload.requires_2fa) {
        if (payload.csrf_token) token = payload.csrf_token;
        reveal2fa();
        return;
      }

      if (payload.redirect) {
        window.location.href = payload.redirect;
      }
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);

      // Неверный код: чистим поле, показываем красную обводку и текст ошибки.
      if (error.errors?.code && codeInput) {
        codeInput.value = '';
        codeInput.classList.remove('is-valid');
        codeInput.classList.add('is-invalid');
        codeInput.focus();
      }
    }
  });
}

function showThanksModal(message) {
  const thanksEl = document.getElementById('modalThanks');
  if (!thanksEl) return;

  const messageEl = thanksEl.querySelector('#thanksMessage');
  if (messageEl && message) {
    messageEl.textContent = message;
  }

  bootstrap.Modal.getOrCreateInstance(thanksEl).show();
}

// Закрываем модалку формы и после её скрытия показываем модалку «Спасибо».
function handleLeadSuccess(form, message) {
  form.reset();
  form.classList.remove('was-validated');
  form.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));

  const modalEl = form.closest('.modal');
  if (!modalEl) {
    showThanksModal(message);
    return;
  }

  modalEl.addEventListener('hidden.bs.modal', () => showThanksModal(message), { once: true });
  bootstrap.Modal.getOrCreateInstance(modalEl).hide();
}

export function requestForm() {
  const form = document.getElementById('requestForm');
  if (!form) return;

  const gaDefaults = { name: 'request_form', location: 'commissions' };
  const captcha = initCaptcha(form, 'lead');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    if (!form.checkValidity()) {
      trackFormError(form, gaDefaults, collectInvalidFields(form));
      return;
    }

    try {
      const payload = await postForm(form.dataset.action, {
        name: form.querySelector('#requestName')?.value,
        email: form.querySelector('#requestEmail')?.value,
        telegram: form.querySelector('#requestTelegram')?.value,
        company: form.querySelector('#requestCompany')?.value,
        captcha_token: await captcha.getToken(),
      });

      captcha.reset();
      trackFormSubmit(form, gaDefaults);
      trackTelegramLead();
      trackMetaLead();
      handleLeadSuccess(form, payload.message);
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
      trackFormError(form, gaDefaults, Object.keys(error.errors || {}).join(','));
    }
  });
}

export function questionForm() {
  const form = document.getElementById('questionForm');
  if (!form) return;

  const gaDefaults = { name: 'faq_form', location: 'faq' };
  const captcha = initCaptcha(form, 'lead');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    if (!form.checkValidity()) {
      trackFormError(form, gaDefaults, collectInvalidFields(form));
      return;
    }

    try {
      const payload = await postForm(form.dataset.action, {
        name: form.querySelector('#questionName')?.value,
        email: form.querySelector('#questionEmail')?.value,
        question: form.querySelector('#questionText')?.value,
        captcha_token: await captcha.getToken(),
      });

      captcha.reset();
      trackFormSubmit(form, gaDefaults);
      handleLeadSuccess(form, payload.message);
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
      trackFormError(form, gaDefaults, Object.keys(error.errors || {}).join(','));
    }
  });
}

// Заявка с полоски-анонса конференции («встретимся на выставке»).
// Отдельная функция, а не переиспользование questionForm: у формы свой набор полей
// (один произвольный контакт вместо email, необязательный рассказ о проекте) и своя
// капча-форма в config/captcha.php.
export function conferenceForm() {
  const form = document.getElementById('conferenceForm');
  if (!form) return;

  const gaDefaults = { name: 'conference_form', location: 'conference_bar' };
  const captcha = initCaptcha(form, 'conference');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    if (!form.checkValidity()) {
      trackFormError(form, gaDefaults, collectInvalidFields(form));
      return;
    }

    try {
      const payload = await postForm(form.dataset.action, {
        name: form.querySelector('#conferenceName')?.value,
        contact: form.querySelector('#conferenceContact')?.value,
        message: form.querySelector('#conferenceMessage')?.value,
        captcha_token: await captcha.getToken(),
      });

      captcha.reset();
      trackFormSubmit(form, gaDefaults);
      trackTelegramLead();
      handleLeadSuccess(form, payload.message);
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
      trackFormError(form, gaDefaults, Object.keys(error.errors || {}).join(','));
    }
  });
}

// Заявка на участие в реферальной программе (страница /referral-program/).
// Отдельная функция, а не переиспользование questionForm: у формы свой набор полей
// (Telegram вместо вопроса), своя капча-форма в config/captcha.php и свой ответ.
export function referralForm() {
  const form = document.getElementById('referralForm');
  if (!form) return;

  const gaDefaults = { name: 'referral_form', location: 'referral_program' };
  const captcha = initCaptcha(form, 'referral');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    if (!form.checkValidity()) {
      trackFormError(form, gaDefaults, collectInvalidFields(form));
      return;
    }

    try {
      const payload = await postForm(form.dataset.action, {
        name: form.querySelector('#referralName')?.value,
        email: form.querySelector('#referralEmail')?.value,
        telegram: form.querySelector('#referralTelegram')?.value,
        captcha_token: await captcha.getToken(),
      });

      captcha.reset();
      trackFormSubmit(form, gaDefaults);
      trackTelegramLead();
      handleLeadSuccess(form, payload.message);
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
      trackFormError(form, gaDefaults, Object.keys(error.errors || {}).join(','));
    }
  });
}

export function forgetForm() {
  const form = document.getElementById('forgetPasswordForm');
  if (!form) return;

  const mainContainer = form.closest('.forget-content')?.parentElement;
  const captcha = initCaptcha(form, 'forgot_password');
  const notice = document.getElementById('forgetNotice');
  const sendButton = document.getElementById('sendBtn');
  const resendBtn = document.getElementById('resendBtn');
  const timerValue = document.getElementById('timerValue');
  const timerState = resendBtn?.querySelector('.timer-state');
  const readyState = resendBtn?.querySelector('.ready-state');

  let resendInterval;

  function startResendTimer() {
    clearInterval(resendInterval);

    let seconds = 60;

    resendBtn.disabled = true;
    resendBtn.classList.add('disabled');

    timerState?.classList.remove('d-none');
    readyState?.classList.add('d-none');

    if (timerValue) {
      timerValue.textContent = seconds;
    }

    resendInterval = setInterval(() => {
      seconds--;

      if (timerValue) {
        timerValue.textContent = seconds;
      }

      if (seconds <= 0) {
        clearInterval(resendInterval);

        if (sendButton) {
          sendButton.disabled = false;
        }

        resendBtn.disabled = false;
        resendBtn.classList.remove('disabled');

        timerState?.classList.add('d-none');
        readyState?.classList.remove('d-none');
      }
    }, 1000);
  }

  async function submitForget() {
    try {
      await postForm(form.dataset.action, {
        email: form.querySelector('#forgetEmail')?.value,
        captcha_token: await captcha.getToken(),
      });
    } finally {
      // Токен одноразовый, а форма остаётся на странице (кнопка «отправить ещё раз») —
      // сбрасываем виджет после каждой попытки.
      captcha.reset();
    }

    mainContainer?.classList.add('sended');
    notice?.classList.remove('d-none');

    if (sendButton) {
      sendButton.disabled = true;
    }

    startResendTimer();
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    try {
      await submitForget();
    } catch (error) {
      showFormErrors(form, error.errors);
    }
  });

  resendBtn?.addEventListener('click', async () => {
    if (resendBtn.disabled) return;

    if (sendButton) {
      sendButton.disabled = true;
    }

    try {
      await submitForget();
    } catch (error) {
      if (sendButton) {
        sendButton.disabled = false;
      }
    }
  });
}

export function resetPasswordForm() {
  const form = document.getElementById('resetPasswordForm');
  if (!form) return;

  const captcha = initCaptcha(form, 'reset_password');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    const password = form.querySelector('#resetPassword')?.value;
    const passwordRepeat = form.querySelector('#resetPasswordRepeat')?.value;

    if (password && passwordRepeat && password !== passwordRepeat) {
      form.querySelector('#resetPasswordRepeat')?.classList.add('is-invalid');
      return;
    }

    if (!form.checkValidity()) return;

    try {
      const payload = await postForm(form.dataset.action, {
        token: form.querySelector('[name="token"]')?.value,
        email: form.querySelector('[name="email"]')?.value,
        password,
        password_confirmation: passwordRepeat || password,
        captcha_token: await captcha.getToken(),
      });

      if (payload.redirect) {
        window.location.href = payload.redirect;
      }
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
    }
  });
}

export function resendVerificationForm() {
  const form = document.getElementById('resendVerificationForm');
  if (!form) return;

  const captcha = initCaptcha(form, 'resend_verification');

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    form.classList.add('was-validated');

    if (!form.checkValidity()) return;

    try {
      await postForm(form.dataset.action, {
        email: form.querySelector('#resendEmail')?.value,
        captcha_token: await captcha.getToken(),
      });

      // Форма остаётся на странице — сбрасываем одноразовый токен для повторной отправки.
      captcha.reset();
      form.querySelector('#resendSuccess')?.classList.remove('d-none');
    } catch (error) {
      captcha.reset();
      showFormErrors(form, error.errors);
    }
  });
}
